import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { RedoIcon } from 'assets/icons';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { convertMileage, convertMileageToCurrentUnits } from 'domains/maintenancePlan/MaintenancePlan.mapper';
import { fetchPlannedOperationsRequestSaga, getPlannedOperations } from 'domains/maintenancePlan/MaintenancePlan.store';
import { GetPlannedOperationsResponseLocal, MILES_TO_KM } from 'domains/maintenancePlan/MaintenancePlan.types';
import { getDealerType } from 'domains/user';
import { MaintenanceEditProps } from 'pages/CatalogPage/DH/Maintenance/Maintenance';
import { SFlexMileage, SInput } from 'pages/CatalogPage/DH/Maintenance/Maintenance.styled';
import { theme } from 'styles';
import { Box, Flex, Icon, notifyTop, RadioGroup, Text, WithTooltip } from 'UI';
import { hasData, SearchData } from 'utils';

const MileageDisplay = ({
  maintenancePlanData,
  getLabel,
  units,
  mileage,
  setMileage,
  val,
  setVal,
  setUnits,
}: MaintenanceEditProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const operationsResponse = useSelector((state: RootState) => getPlannedOperations(state, vehicleDetail?.vin));
  const unitsKm = units ?? 'km';
  const dealerType = useSelector(getDealerType);

  const onChangeUnits = () => {
    const mileageConverted = convertMileage(unitsKm, mileage ?? 0);
    switch (units) {
      case 'miles':
        setUnits('km');
        break;
      case 'km':
        setUnits('miles');
        break;
      default:
        setUnits('miles');
    }
    setMileage(mileageConverted);
    setVal(mileageConverted ? String(mileageConverted) : '0');
  };

  function setMileageFromString(inputVal: string) {
    setMileage(Number.parseInt(inputVal, 10));
  }

  function handleUpdateVal(inputVal: string, operations: SearchData<GetPlannedOperationsResponseLocal>) {
    if ((operations.data?.operations ?? []).length > 0) {
      const firstItem = operations.data?.operations[0];
      const apiUnits = firstItem?.mileageUnit ?? 'km';
      const apiMileage = firstItem?.usedVehicleMileage ?? 0;
      if (apiMileage > 0) {
        const apiMileageToCurrentUnits = convertMileageToCurrentUnits(
          unitsKm,
          apiUnits,
          operations.data?.baseMileage ?? 0,
        );
        if (Number.parseInt(inputVal, 10) >= apiMileageToCurrentUnits) {
          setMileageFromString(inputVal);
        } else {
          notifyTop(
            'warning',
            t(
              'catalog.maintenance.mileage_entered_higher_than_initial',
              'The mileage value entered must not be less than the initial value',
            ),
          );
          setMileage(apiMileageToCurrentUnits);
          setVal(apiMileageToCurrentUnits ? String(apiMileageToCurrentUnits) : '0');
        }
      } else {
        setMileageFromString(inputVal);
      }
    }
  }

  function onMileageBlur(value: string) {
    if (!hasData(operationsResponse) || (operationsResponse.data?.operations ?? []).length === 0) {
      setMileage(Number.parseInt(value, 10));
      return;
    }
    handleUpdateVal(value, operationsResponse);
  }

  useEffect(() => {
    if (dealerType === 'R1' || dealerType === 'R2') {
      dispatch(fetchPlannedOperationsRequestSaga({ mileageKm: 0 }));
    }
  }, [dispatch, dealerType]);

  if (maintenancePlanData.operationByMileageOrTimeList.length === 0) {
    return <></>;
  }

  return (
    <>
      <Text type={'h5_bold'}>{getLabel(27)}</Text>
      {(dealerType === 'R1' || dealerType === 'R2') && (
        <>
          <Flex direction={'row'} align={'center'}>
            <Text type="base">{t('catalog.maintenance.units.select', 'Select display mode:')}</Text>
            <Box width={10} />
            <RadioGroup
              value={unitsKm}
              optionType={'button'}
              buttonStyle={'solid'}
              options={[
                { label: t('catalog.maintenance.units_km', 'Km'), value: 'km' },
                { label: t('catalog.maintenance.units_miles', 'Miles'), value: 'miles' },
              ]}
              onChange={onChangeUnits}
            />
          </Flex>
          <SFlexMileage direction={'row'} align={'center'} padding={15} maxWidth={1400} gap={15}>
            <Text type="base">
              {t(
                'catalog.maintenance.mileage.descriptions',
                'Please enter the mileage displayed in the vehicle if the mileage taken into account is not consistent. \n' +
                  "The mileage will be taken into account for the calculation of the manufacturer's maintenance recommendations.",
              )}
            </Text>
            <Box width={200}>
              <SInput
                maxLength={7}
                onBlur={onMileageBlur}
                isHighlighted={false}
                bordered
                placeholder={t('catalog.maintenance.mileage', 'Mileage')}
                value={val}
                onChange={setVal}
              />
            </Box>
            <WithTooltip title={t('catalog.maintenance.refresh_mileage', 'Refresh mileage')}>
              <Icon
                IconComponent={RedoIcon}
                color={theme.color.clear_blue}
                size={24}
                onClick={() => {
                  if (mileage && (dealerType === 'R1' || dealerType === 'R2')) {
                    const mileageKm = unitsKm === 'km' ? mileage : Math.ceil(mileage * MILES_TO_KM);
                    dispatch(fetchPlannedOperationsRequestSaga({ mileageKm }));
                  }
                }}
              />
            </WithTooltip>
          </SFlexMileage>
        </>
      )}
    </>
  );
};

export default MileageDisplay;
